/**
 * The standards write-back queue (Sprint 09 F5; ADR-0038 R7, Ground Truth §11.3).
 *
 * Challenges to a FINDING belong to the deal they were raised on; challenges to the SCORING STANDARD
 * belong to everyone, so they collect here — across every deal in the tenant — and are settled by a new
 * version of the standard rather than one at a time.
 *
 * ## The distinction this page has to keep straight
 *
 * It shows the version each argument was raised against next to the version in force now. It does NOT
 * mark anything "addressed" because the standard has since moved: a version bump is evidence that the
 * standard changed, never evidence that it changed BECAUSE of this argument. A queue that quietly
 * marks its own items resolved is how a system stops hearing the field.
 */

import { useEffect, useState } from 'react';
import { getWriteBackQueue, type WriteBackItem, type WriteBackQueue } from '../api.js';
import { useChrome } from '../chrome.js';
import { AnalyzerLink } from '../components/AnalyzerLink.js';
import { BackLink } from '../components/BackLink.js';
import { useSession } from '../useSession.js';
import { timestamp } from '../format.js';

type Load = { status: 'loading' } | { status: 'ready'; queue: WriteBackQueue | null };

/** How an answered item reads. `partial` sits with accepted: part of the argument landed. */
export function outcomeLabel(item: WriteBackItem): string {
  if (item.status === 'open') return 'Awaiting review';
  if (item.resolution === 'rejected') return 'Reviewed — standard unchanged';
  if (item.resolution === 'partial') return 'Partly agreed — a change is owed';
  return 'Agreed — a change is owed';
}

/** The chip tone for an outcome. Gold (`partial`) marks the ones that still owe a version. */
export function outcomeTone(item: WriteBackItem): string {
  if (item.status === 'open') return 'partial';
  if (item.resolution === 'rejected') return 'structural';
  return 'fixable';
}

/**
 * What the standard currently owes, in one line. Exported for a direct test: it is the only sentence
 * on the page that changes with the data, and "0 awaiting review" beside an empty state was the first
 * version's mistake — a zero presented as a status.
 */
export function owedSummary(counts: { open: number; agreed: number; declined: number }): string {
  const parts: string[] = [];
  if (counts.agreed > 0) {
    parts.push(`${counts.agreed} accepted and owed a change`);
  }
  if (counts.open > 0) {
    parts.push(`${counts.open} awaiting review`);
  }
  if (counts.declined > 0) {
    parts.push(`${counts.declined} reviewed and declined`);
  }
  return parts.join(' · ');
}

/**
 * One argument in the queue.
 *
 * Extracted from the page so it can be RENDERED in a test. The page itself needs a session, a router
 * and an effect, so a suite that can only reach the pure label helpers proves the wording of a chip and
 * nothing about what a reader actually sees — which is how `supportingDocumentId` sat unrendered here
 * while `outcomeLabel` had six tests. `ChallengeLedgerCard` is exported for exactly this reason.
 */
export function WriteBackRow({
  item,
  currentStandardVersion,
}: {
  item: WriteBackItem;
  currentStandardVersion: string;
}) {
  return (
    <li className={`stdq__row stdq__row--${item.status}`}>
      <div className="stdq__head">
        <span className="stdq__target">{item.target}</span>
        <span className={`chip chip--${outcomeTone(item)}`}>{outcomeLabel(item)}</span>
      </div>
      <p className="stdq__claim">{item.claim}</p>
      <p className="stdq__meta muted">
        Raised by {item.challengerName} · {timestamp(item.createdAt)} · against standard{' '}
        {item.raisedAgainstVersion}
        {/*
          The challenger's cited evidence. This queue is where an argument about the SCORING STANDARD
          is weighed, and it was showing the argument while silently dropping the fact that the arguer
          attached a document to support it — the deal-level panel has said "document attached" all
          along (Challenges.tsx). Presence, not the id: a bare uuid is not a fact a reader can act on,
          and this deliberately matches the treatment already in the product.
        */}
        {item.supportingDocumentId ? ' · document attached' : ''}
        {item.standardMovedSince
          ? ` · the standard has since moved to ${currentStandardVersion} — check whether this was covered`
          : ''}
      </p>
      {item.reasoning && (
        <div className="stdq__answer">
          <p className="stdq__reason">{item.reasoning}</p>
          <p className="stdq__meta muted">
            Reviewed by {item.answeredByName} · {item.answeredAt ? timestamp(item.answeredAt) : ''}
          </p>
        </div>
      )}
      {/*
        🔴 `AnalyzerLink`, not an in-app navigate (#1399). This queue is routed in the Hub, which has no
        `/analyses` route — the old button was redirected to `/projects`, so "Open the deal" never
        opened it. Cross-origin to the analyzer on the Hub; in-app on the analyzer.
      */}
      <AnalyzerLink className="stdq__link" to={`/analyses/${encodeURIComponent(item.analysisId)}`}>
        Open the deal it came from
      </AnalyzerLink>
    </li>
  );
}

export function StandardsQueue() {
  const session = useSession();
  // Supplied by the application root — `AppHeader` in the Deal Analyzer, `HubHeader` in the Hub.
  // This page is rendered by both and no longer decides which product it belongs to (see chrome.tsx).
  const Chrome = useChrome();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    if (session.status !== 'authed') return;
    let cancelled = false;
    void getWriteBackQueue().then((queue) => {
      if (!cancelled) setLoad({ status: 'ready', queue });
    });
    return () => {
      cancelled = true;
    };
  }, [session.status]);

  if (session.status !== 'authed' || load.status === 'loading') {
    return <div className="app__loading">Loading…</div>;
  }

  const queue = load.queue;

  return (
    <div className="app">
      {Chrome !== null && <Chrome claims={session.claims} />}
      <main className="app__main">
        <div className="app__crumbs">
          <BackLink fallback="/" label="Back" />
        </div>
        <div className="app__intro">
          <h1>Scoring standard</h1>
          <p className="sub">
            Where someone has argued that a rule itself is wrong — not that a particular deal was
            scored wrong. These are settled by a new version of the standard, which then applies to
            every deal at once.
          </p>
        </div>

        {!queue ? (
          <div className="notice">We couldn’t load the queue just now. Refresh to try again.</div>
        ) : (
          <>
            <section className="card">
              <h3 className="card__title">Standard in force</h3>
              <p className="stdq__version">{queue.currentStandardVersion}</p>
              {/* Nothing to say when nothing is owed. "0 awaiting review" above an empty state that
                  already says nobody has challenged anything is two elements reporting the same
                  nothing — and a zero presented as a status reads like a metric that failed to load. */}
              {queue.items.length > 0 && <p className="card__note">{owedSummary(queue.counts)}</p>}
            </section>

            {queue.items.length === 0 ? (
              <section className="card">
                <p className="card__prose">
                  Nobody has challenged the scoring standard yet. When someone believes a threshold
                  or a weight is set wrong, they raise it from the deal it showed up on, and it
                  lands here.
                </p>
              </section>
            ) : (
              <section className="card">
                <h3 className="card__title">Raised against the standard</h3>
                <ol className="stdq__list">
                  {queue.items.map((item) => (
                    <WriteBackRow
                      key={item.id}
                      item={item}
                      currentStandardVersion={queue.currentStandardVersion}
                    />
                  ))}
                </ol>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
